import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button as MuiButton,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
} from '@mui/material';
import {
  Search,
  Settings,
  Home,
  AddCircleOutline,
  Menu as MenuIcon,
  Login,
  PersonAddAlt,
  Logout,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './primitives';
import skateSkoutLogo from '../assets/icons/skate_skout_logo.png';

const navigationItems = [
  {
    name: 'find-events',
    label: 'Find Events',
    icon: <Search fontSize="small" />,
    path: '/',
  },
  {
    name: 'facilities',
    label: 'Facilities',
    icon: <Home fontSize="small" />,
    path: '/facilities',
  },
  {
    name: 'add-events',
    label: 'Add Events',
    icon: <AddCircleOutline fontSize="small" />,
    path: '/create-facility-resource-event',
  },
  {
    name: 'settings',
    label: 'Settings',
    icon: <Settings fontSize="small" />,
    path: '/settings',
  },
];

const TopNavigation = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  // Resizing up to desktop unmounts the trigger, leaving the menu anchored to a
  // detached node.
  useEffect(() => {
    if (!isMobile) {
      setAnchorEl(null);
    }
  }, [isMobile]);

  const handleNavigation = (path) => {
    if (path) {
      navigate(path);
    }
  };

  const handleSignOut = async () => {
    await logout();
    navigate('/');
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  const handleSignUp = () => {
    navigate('/signup');
  };

  const authActions = isAuthenticated
    ? [
        {
          name: 'sign-out',
          label: 'Sign Out',
          icon: <Logout fontSize="small" />,
          variant: 'outlined',
          onClick: handleSignOut,
        },
      ]
    : [
        {
          name: 'sign-in',
          label: 'Sign In',
          icon: <Login fontSize="small" />,
          variant: 'outlined',
          onClick: handleSignIn,
        },
        {
          name: 'sign-up',
          label: 'Sign Up',
          icon: <PersonAddAlt fontSize="small" />,
          variant: 'contained',
          onClick: handleSignUp,
        },
      ];

  // On mobile only Sign Up stays in the bar, so the dropdown drops it to avoid
  // offering the same action twice.
  const barActions = isMobile
    ? authActions.filter((action) => action.name === 'sign-up')
    : authActions;
  const menuActions = authActions.filter((action) => action.name !== 'sign-up');

  const handleMenuAction = (action) => () => {
    setAnchorEl(null);
    action();
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Toolbar
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          minHeight: 64,
          px: { xs: 1, sm: 2 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <MuiButton
            onClick={() => handleNavigation('/')}
            aria-label="SkateSkout home"
            sx={{
              px: 1.5,
              minWidth: 'auto',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <Box
              component="img"
              src={skateSkoutLogo}
              alt="SkateSkout"
              sx={{
                height: { xs: 22, md: 36 },
                width: 'auto',
                display: 'block',
              }}
            />
          </MuiButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          {isMobile ? (
            <MuiButton
              id="mobile-nav-button"
              onClick={(event) => setAnchorEl(event.currentTarget)}
              startIcon={<MenuIcon />}
              aria-haspopup="menu"
              aria-controls={menuOpen ? 'mobile-nav-menu' : undefined}
              aria-expanded={menuOpen ? 'true' : undefined}
              sx={{
                color: theme.palette.text.primary,
                textTransform: 'uppercase',
                fontWeight: 600,
                fontSize: '0.8rem',
                borderRadius: 0,
                px: 1.5,
                py: 1.5,
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              Menu
            </MuiButton>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
              {navigationItems.map((item) => (
                <MuiButton
                  key={item.name}
                  onClick={() => handleNavigation(item.path)}
                  startIcon={item.icon}
                  sx={{
                    color: theme.palette.text.primary,
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    borderRadius: 0,
                    px: 1.5,
                    py: 1.5,
                    whiteSpace: 'nowrap',
                    backgroundColor: 'transparent',
                    borderLeft: `1px solid ${theme.palette.divider}`,
                    borderRight: `1px solid ${theme.palette.divider}`,
                    ml: '-1px',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  {item.label}
                </MuiButton>
              ))}
            </Box>
          )}
        </Box>

        {barActions.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexShrink: 0,
            }}
          >
            {barActions.map((action) => (
              <Button
                key={action.name}
                variant={action.variant}
                onClick={action.onClick}
              >
                {action.label}
              </Button>
            ))}
          </Box>
        )}
      </Toolbar>

      <Menu
        id="mobile-nav-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        MenuListProps={{ 'aria-labelledby': 'mobile-nav-button' }}
        PaperProps={{ sx: { width: 'min(260px, calc(100vw - 32px))' } }}
      >
        {navigationItems.map((item) => (
          <MenuItem
            key={item.name}
            onClick={handleMenuAction(() => handleNavigation(item.path))}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}

        <Divider />

        {menuActions.map((action) => (
          <MenuItem key={action.name} onClick={handleMenuAction(action.onClick)}>
            <ListItemIcon>{action.icon}</ListItemIcon>
            <ListItemText>{action.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </AppBar>
  );
};

export default TopNavigation;
